import { type AllEvents } from '../types';

type EventBusAction<K extends keyof AllEvents = keyof AllEvents> =
  K extends keyof AllEvents
    ? AllEvents[K] extends () => void
      ? { type: K }
      : AllEvents[K] extends (payload: infer P) => void
        ? { type: K; payload: P }
        : never
    : never;

class EventBus {
  actionListenersMap: Map<keyof AllEvents, Array<AllEvents[keyof AllEvents]>>;
  emittingEvents: boolean = false;
  constructor() {
    this.actionListenersMap = new Map();
  }

  startEmitting() {
    this.emittingEvents = true;
  }

  stopEmitting() {
    this.emittingEvents = false;
  }

  publish<K extends keyof AllEvents>(
    action: EventBusAction<K>,
    forceEmit = false,
  ) {
    const isInternalEvent = action.type.startsWith('internal.');
    if (!this.emittingEvents && !forceEmit && !isInternalEvent) {
      return;
    }
    const listeners = this.actionListenersMap.get(action.type);
    if (!listeners) {
      return;
    }

    for (const listener of listeners) {
      if (typeof listener === 'function') {
        // Use type discrimination to call the listener correctly
        if ('payload' in action) {
          // We know this is a listener that expects a payload
          const payloadListener = listener as (
            payload: Parameters<AllEvents[K]>[0],
          ) => void;
          payloadListener(action.payload);
        } else {
          // We know this is a listener that expects no parameters
          const noPayloadListener = listener as () => void;
          noPayloadListener();
        }
      }
    }
  }

  // Subscribe to actions
  subscribe<K extends keyof AllEvents>(
    actionType: K,
    listener: AllEvents[K],
    options?: { signal?: AbortSignal },
  ) {
    // If already aborted, don't subscribe
    if (options?.signal?.aborted) {
      return () => {};
    }

    if (!this.actionListenersMap.get(actionType)) {
      this.actionListenersMap.set(actionType, []);
    }
    this.actionListenersMap.get(actionType)?.push(listener);

    // Create unsubscribe function
    const unsubscribe = () => {
      const listenersList = this.actionListenersMap.get(actionType);
      if (listenersList) {
        this.actionListenersMap.set(
          actionType,
          listenersList.filter(l => l !== listener),
        );
      }
    };

    // If signal provided, auto-unsubscribe on abort
    if (options?.signal) {
      options.signal.addEventListener('abort', unsubscribe, { once: true });
    }

    return unsubscribe;
  }
}

// Export a singleton instance
export const eventBus = new EventBus();
